import React, { useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import { useFulfilmentLocationContext } from '../../../components/FulfilmentLocationProvider';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Alert from '@material-ui/lab/Alert';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import Icon from '@afosto/app-components-react/Icon';
import reorderArray from '@afosto/utils/array/reorderArray';
import { fulfilment as fulfilmentApi } from '@afosto/fulfilment/api/Fulfilment';
import styled from 'styled-components';
import translations from './translations';

const DraggableContainer = styled(Card)`
  && {
    user-select: none;
    margin-bottom: ${props => props.theme.spacing()}px;
    padding: ${props => props.theme.spacing(2)}px;
  }
`;

const DraggableTitle = styled(Typography)`
  && {
    margin-left: ${props => props.theme.spacing(2)}px;
  }
`;

const FulfilmentLocationSorting = () => {
  const [hasPermissions] = usePermissions(['lcs:locations:write']);
  const intl = useIntl();
  const { fulfilmentLocation, fulfilmentPositions } = useFulfilmentLocationContext();
  const { enqueueSnackbar } = useSnackbar();
  const [positions, setPositions] = useState((fulfilmentPositions ?? []).map(item => item.code));
  const [isUpdatingPositions, setIsUpdatingPositions] = useState(false);

  /**
   * Update the positions.
   * @param {array} positionedList
   */
  const updatePositions = async positionedList => {
    setIsUpdatingPositions(true);

    try {
      await fulfilmentApi.updatePositionOrder(fulfilmentLocation.id, positionedList);
      return true;
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.positionsNotUpdated), {
        variant: 'error',
      });

      return Promise.reject(err);
    } finally {
      setIsUpdatingPositions(false);
    }
  };

  /**
   * Handle resorting the positions list based on drop position.
   * @param {object} result
   */
  const handleDragEnd = async result => {
    const oldPositions = [...positions];

    try {
      const { source, destination } = result;

      if (!destination) {
        return;
      }

      const repositionedList = reorderArray(positions, {
        start: source.index,
        end: destination.index,
      });

      setPositions(repositionedList);

      await updatePositions(repositionedList);
    } catch (err) {
      setPositions(oldPositions);
    }
  };

  if (positions?.length >= 250) {
    return (
      <Alert severity="info">
        <FormattedMessage {...translations.tooManyPositionsMessage} />
      </Alert>
    );
  }

  return (
    <>
      {hasPermissions() && (
        <Box pb={2}>
          <Typography variant="body2">
            <FormattedMessage {...translations.updatePositionsMessage} />
          </Typography>
        </Box>
      )}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="sorting">
          {provided => (
            <div ref={provided.innerRef}>
              {positions.map((position, idx) => (
                <Draggable
                  key={position}
                  draggableId={position}
                  index={idx}
                  isDragDisabled={isUpdatingPositions || !hasPermissions()}
                >
                  {dragProvided => (
                    <DraggableContainer
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                    >
                      <Box display="flex" alignItems="center">
                        {hasPermissions() && <Icon icon={['fal', 'grip-lines']} />}
                        <DraggableTitle variant="body2">{position}</DraggableTitle>
                      </Box>
                    </DraggableContainer>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default FulfilmentLocationSorting;
